import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Divider,
} from '@mui/material';
import {
  School,
  CheckCircle,
  Schedule,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import { Course, Progress } from '../types';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [recentCourses, setRecentCourses] = useState<Course[]>([]);
  const [userProgress, setUserProgress] = useState<Progress[]>([]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        
        // Obtener cursos recomendados
        const recommendedCourses = await apiService.getRecommendedCourses();
        setRecentCourses(recommendedCourses.slice(0, 3));

        // Obtener progreso del usuario
        const progress = await apiService.getUserProgress();
        console.log('📊 Progreso del usuario en dashboard:', progress);
        console.log('📈 Estados de progreso:', progress.map(p => ({ 
          course: typeof p.course === 'string' ? p.course : p.course.title, 
          status: p.status,
          completionPercentage: p.completionPercentage 
        })));
        setUserProgress(progress);

      } catch (error) {
        console.error('Error al cargar datos del dashboard:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [user]);


  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header del Dashboard */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
          📚 Mis Cursos
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Bienvenido, {user?.firstName}. Aquí tienes un resumen de tu progreso de aprendizaje.
        </Typography>
      </Box>

      {/* Estadísticas principales */}
      <Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: 'wrap' }}>
        <Card elevation={2} sx={{ flex: 1, minWidth: 250 }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography color="text.secondary" gutterBottom>
                  Cursos Completados
                </Typography>
                <Typography variant="h4" component="div" fontWeight="bold">
                  {userProgress.filter(p => p.status === 'completed').length}
                </Typography>
              </Box>
              <Avatar sx={{ bgcolor: 'success.main' }}>
                <CheckCircle />
              </Avatar>
            </Box>
          </CardContent>
        </Card>

        <Card elevation={2} sx={{ flex: 1, minWidth: 250 }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography color="text.secondary" gutterBottom>
                  Cursos en Progreso
                </Typography>
                <Typography variant="h4" component="div" fontWeight="bold">
                  {userProgress.filter(p => p.status === 'in_progress').length}
                </Typography>
              </Box>
              <Avatar sx={{ bgcolor: 'info.main' }}>
                <Schedule />
              </Avatar>
            </Box>
          </CardContent>
        </Card>

        <Card elevation={2} sx={{ flex: 1, minWidth: 250 }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography color="text.secondary" gutterBottom>
                  Total de Cursos
                </Typography>
                <Typography variant="h4" component="div" fontWeight="bold">
                  {userProgress.length}
                </Typography>
              </Box>
              <Avatar sx={{ bgcolor: 'primary.main' }}>
                <School />
              </Avatar>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Progreso de cursos */}
      <Card elevation={2} sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>
            📊 Progreso de Cursos
          </Typography>

          {userProgress.length > 0 ? (
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {userProgress.slice(0, 6).map((progress, index) => {
                const course = typeof progress.course === 'string' ? null : progress.course;
                const courseId = course?._id || course?.id || progress.course;
                const paperKey = progress.id || (typeof courseId === 'string' ? courseId : course?._id) || `progress-${index}`;
                
                return (
                  <Paper 
                    key={paperKey} 
                    sx={{ 
                      p: 3, 
                      border: '1px solid', 
                      borderColor: 'divider', 
                      flex: 1, 
                      minWidth: 350,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        boxShadow: 3,
                        borderColor: 'primary.main',
                        transform: 'translateY(-2px)'
                      }
                    }}
                    onClick={() => navigate(`/courses/${courseId}`)}
                  >
                    <Typography 
                      variant="h6" 
                      fontWeight="bold" 
                      gutterBottom
                      sx={{ 
                        color: 'primary.main',
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      {course?.title || (typeof progress.course === 'string' ? progress.course : 'Curso')}
                    </Typography>
                    
                    {course?.description && (
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        sx={{ mb: 2, lineHeight: 1.4 }}
                      >
                        {course.description.length > 120 
                          ? `${course.description.substring(0, 120)}...` 
                          : course.description}
                      </Typography>
                    )}
                    
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                      <LinearProgress
                        variant="determinate"
                        value={progress.completionPercentage}
                        sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                        color={progress.status === 'completed' ? 'success' : 'primary'}
                      />
                      <Typography variant="body2" color="text.secondary" fontWeight="bold">
                        {Math.round(progress.completionPercentage)}%
                      </Typography>
                    </Box>
                    
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Chip
                        label={progress.status === 'completed' ? 'Completado' : 
                               progress.status === 'in_progress' ? 'En Progreso' : 'No Iniciado'}
                        size="small"
                        color={progress.status === 'completed' ? 'success' : 
                               progress.status === 'in_progress' ? 'primary' : 'default'}
                      />
                      
                      {course && (
                        <Box display="flex" gap={1}>
                          <Chip
                            label={course.difficulty}
                            size="small"
                            variant="outlined"
                            color="secondary"
                          />
                          <Chip
                            label={`${course.estimatedDuration} min`}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                      )}
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          ) : (
            <Alert severity="info">
              No tienes cursos en progreso. ¡Comienza tu primer curso!
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Cursos recomendados */}
      <Card elevation={2}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
            <Typography variant="h6" fontWeight="bold">
              📚 Cursos Recomendados
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate('/courses')}
            >
              Ver todos
            </Button>
          </Box>

          {recentCourses.length > 0 ? (
            <List>
              {recentCourses.map((course, index) => (
                <React.Fragment key={course.id || course._id || index}>
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => navigate(`/courses/${course.id || course._id}`)}
                      sx={{ borderRadius: 2, mb: 1 }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          <School />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={course.title}
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {course.description}
                            </Typography>
                            <Box display="flex" gap={1} mt={1}>
                              <Chip
                                label={course.difficulty}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                              <Chip
                                label={`${course.estimatedDuration} min`}
                                size="small"
                                variant="outlined"
                              />
                            </Box>
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                  {index < recentCourses.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          ) : (
            <Alert severity="info">
              No hay cursos recomendados disponibles en este momento.
            </Alert>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Dashboard; 